/**
 * Shared layout primitive for every auth screen.
 *
 * Per docs/design.md § Editorial mode (auth screens fall in this bucket):
 *   - cream bg (#FAFAF7)
 *   - Space Grotesk display heading, weight 500, tracking-tight
 *   - Space Mono eyebrow with 6px cobalt dot indicator
 *   - 16px-radius card with `shadow-card`
 *   - cobalt (#0139B7) accent reserved for primary CTAs + focused inputs
 */

import type { ReactNode } from 'react';

export interface AuthCardProps {
  eyebrow: string;
  title: string;
  lede?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthCard({ eyebrow, title, lede, children, footer }: AuthCardProps): JSX.Element {
  return (
    <main className="min-h-screen bg-bg text-ink">
      <div className="mx-auto flex min-h-screen max-w-page items-center justify-center px-page-pad-x py-20">
        <div className="w-full max-w-md">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="mt-6 font-sans text-h1 font-medium tracking-tight">
            {title}
          </h1>
          {lede ? (
            <p className="mt-4 font-sans text-body-lg text-muted">{lede}</p>
          ) : null}
          <div className="mt-10 rounded-lg border border-line bg-card p-6 shadow-card">
            {children}
          </div>
          {footer ? (
            <p className="mt-6 font-sans text-small text-muted">{footer}</p>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function Eyebrow({ children }: { children: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-eyebrow font-medium uppercase text-muted">
      <span
        aria-hidden
        className="inline-block rounded-pill bg-accent"
        style={{ width: 6, height: 6 }}
      />
      {children}
    </span>
  );
}
